"use client";

import { ArrowLeft, Clock, Users, Star, Wine } from 'lucide-react';
import Link from 'next/link';
import { ImageWithFallback } from '@/figma/components/figma/ImageWithFallback';
import type { TourDetailContent } from '@/lib/page-content-types';

// Mock tour data
const tourData = {
  slug: 'judean-hills-premium',
  name: 'Judean Hills Premium Wine Route',
  tagline: 'Discover Jerusalem\'s elevated wine country',
  duration: 'Full Day (8 hours)',
  groupSize: '2-8 participants',
  price: '$185 per person',
  rating: 4.9,
  reviews: 142,
  image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200',
  
  overview: 'Experience the finest wineries in the Judean Hills, Israel\'s most prestigious wine region. This intimate tour visits three world-class estates, includes private tastings with winemakers, and a gourmet lunch overlooking ancient vineyards.',
  
  highlights: [
    'Visit three premium wineries in the Jerusalem hills',
    'Private tastings of limited-production wines',
    'Meet winemakers and tour barrel cellars',
    'Gourmet lunch at estate vineyard',
    'Stunning views of Judean terroir',
    'Expert guide and sommelier',
    'Transportation from Jerusalem/Tel Aviv',
    'Small group experience (max 8 people)'
  ],
  
  itinerary: [
    {
      time: '08:00',
      title: 'Pickup & Departure',
      description: 'Meet at Jerusalem central location. Scenic drive through Judean Hills with wine region introduction.',
      duration: '45 min'
    },
    {
      time: '09:00',
      title: 'Domaine du Castel',
      description: 'Tour Israel\'s most iconic winery. Visit barrel cellar, vineyard walk, tasting of Grand Vin and reserve wines.',
      duration: '2 hours',
      wines: '5 wines including flagship Grand Vin'
    },
    {
      time: '11:30',
      title: 'Tzora Vineyards',
      description: 'Explore ancient limestone terroir. Winemaker-led tasting of single-vineyard wines. Stunning valley views.',
      duration: '2 hours',
      wines: '4 wines including Shoresh single-vineyard series'
    },
    {
      time: '13:30',
      title: 'Gourmet Lunch',
      description: 'Three-course meal at Clos de Gat overlooking estate vineyards. Wine pairings with each course.',
      duration: '1.5 hours',
      wines: 'Wine pairing with lunch'
    },
    {
      time: '15:30',
      title: 'Clos de Gat',
      description: 'Final winery visit. Tour sustainable vineyard, state-of-the-art cellar. Tasting of current releases.',
      duration: '1.5 hours',
      wines: '4 wines including Syrah and Bordeaux blends'
    },
    {
      time: '17:00',
      title: 'Return Journey',
      description: 'Relaxed drive back with discussion and Q&A. Drop-off at Jerusalem/Tel Aviv locations.',
      duration: '1 hour'
    }
  ],
  
  included: [
    'All winery visits and tastings (13+ wines)',
    'Three-course gourmet lunch with wine pairings',
    'Expert wine guide and sommelier',
    'Comfortable transportation (Mercedes van)',
    'Pickup and drop-off in Jerusalem/Tel Aviv',
    'Bottled water and snacks',
    'Tasting notes and region map'
  ],
  
  notIncluded: [
    'Wine purchases (available at each winery)',
    'Gratuities (optional)',
    'Travel insurance'
  ],
  
  whatToBring: [
    'Comfortable walking shoes',
    'Light jacket (cellars can be cool)',
    'Camera for vineyard photos',
    'Sunscreen and hat',
    'Valid ID (21+ required)'
  ],
  
  dates: [
    { date: 'Sun, Feb 16, 2026', availability: 'Available', spots: 4 },
    { date: 'Wed, Feb 19, 2026', availability: 'Available', spots: 6 },
    { date: 'Sat, Feb 22, 2026', availability: 'Limited', spots: 2 },
    { date: 'Sun, Feb 23, 2026', availability: 'Available', spots: 5 },
    { date: 'Thu, Feb 27, 2026', availability: 'Available', spots: 7 }
  ],
  
  cancellationPolicy: 'Full refund if cancelled 72+ hours before tour. 50% refund 24-72 hours before. No refund within 24 hours.',
  
  minimumAge: '21 years',
  accessibility: 'Moderate walking required. Some wineries have stairs. Contact us for accessibility concerns.',
  languages: 'English, Hebrew (French available on request)'
};

export function TourDetailPage({ tour = tourData }: { tour?: TourDetailContent }) {

  return (
    <div
      className="min-h-screen"
      data-tour-id={tour.slug}
      style={{ fontFamily: 'var(--font-body)' }}
    >
      {/* Back navigation */}
      <div style={{ backgroundColor: 'var(--linen)', borderBottom: '1px solid var(--stone)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16 py-8">
          <Link 
            href="/tours"
            className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
            style={{ 
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: 'var(--charcoal)',
              fontWeight: 400
            }}
          >
            <ArrowLeft size={16} />
            Back to Tours
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="grid lg:grid-cols-5 gap-16">
              <div className="lg:col-span-3">
                <h1 
                  className="mb-4"
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-5xl)',
                    fontWeight: 300,
                    lineHeight: 'var(--line-height-tight)',
                    color: 'var(--charcoal)',
                    letterSpacing: '-0.02em'
                  }}
                >
                  {tour.name}
                </h1>

                <p 
                  className="mb-8"
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-xl)',
                    fontWeight: 300,
                    lineHeight: 1.4,
                    color: 'var(--slate)',
                    fontStyle: 'italic'
                  }}
                >
                  {tour.tagline}
                </p>

                <div className="flex items-center gap-6 mb-10 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Star size={20} style={{ color: 'var(--oxblood)', fill: 'var(--oxblood)' }} />
                    <span 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xl)',
                        color: 'var(--charcoal)',
                        fontWeight: 300
                      }}
                    >
                      {tour.rating}
                    </span>
                    <span 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      ({tour.reviews} reviews)
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock size={18} style={{ color: 'var(--slate)' }} />
                    <span 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-base)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {tour.duration}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Users size={18} style={{ color: 'var(--slate)' }} />
                    <span 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-base)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {tour.groupSize}
                    </span>
                  </div>
                </div>

                <div className="aspect-[16/9] overflow-hidden mb-10" style={{ backgroundColor: 'var(--stone)' }}>
                  <ImageWithFallback
                    src={tour.image}
                    alt={tour.name}
                    className="h-full w-full object-cover"
                  />
                </div>

                <p 
                  className="mb-12"
                  style={{ 
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-lg)',
                    lineHeight: 'var(--line-height-body)',
                    color: 'var(--slate)',
                    fontWeight: 300
                  }}
                >
                  {tour.overview}
                </p>

                {/* Highlights */}
                <div className="mb-16">
                  <h2 
                    className="mb-6"
                    style={{ 
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-2xl)',
                      fontWeight: 300,
                      color: 'var(--charcoal)'
                    }}
                  >
                    Tour Highlights
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {tour.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div 
                          className="mt-1"
                          style={{ 
                            width: '6px',
                            height: '6px',
                            backgroundColor: 'var(--oxblood)',
                            borderRadius: '50%',
                            flexShrink: 0
                          }}
                        />
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-base)',
                            lineHeight: 'var(--line-height-body)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {highlight}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Detailed Itinerary */}
                <div className="mb-16">
                  <h2 
                    className="mb-8"
                    style={{ 
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-2xl)',
                      fontWeight: 300,
                      color: 'var(--charcoal)'
                    }}
                  >
                    Detailed Itinerary
                  </h2>
                  <div className="space-y-8">
                    {tour.itinerary.map((item, index) => (
                      <div 
                        key={index}
                        className="flex gap-8 pb-8"
                        style={{ borderBottom: index < tour.itinerary.length - 1 ? '1px solid var(--stone)' : 'none' }}
                      >
                        <div className="flex-shrink-0 w-20">
                          <div 
                            className="px-3 py-2 text-center"
                            style={{ backgroundColor: 'var(--linen)' }}
                          >
                            <span 
                              style={{ 
                                fontFamily: 'var(--font-body)',
                                fontSize: 'var(--text-base)',
                                color: 'var(--charcoal)',
                                fontWeight: 500
                              }}
                            >
                              {item.time}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 
                            className="mb-2"
                            style={{ 
                              fontFamily: 'var(--font-display)',
                              fontSize: 'var(--text-xl)',
                              fontWeight: 300,
                              color: 'var(--charcoal)'
                            }}
                          >
                            {item.title}
                          </h3>
                          <p 
                            className="mb-3"
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-base)',
                              lineHeight: 'var(--line-height-body)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {item.description}
                          </p>
                          <div className="flex items-center gap-4 flex-wrap">
                            <span 
                              className="px-3 py-1"
                              style={{ 
                                backgroundColor: 'var(--olive-mist)',
                                fontFamily: 'var(--font-body)',
                                fontSize: 'var(--text-xs)',
                                color: 'var(--charcoal)',
                                fontWeight: 400
                              }}
                            >
                              {item.duration}
                            </span>
                            {item.wines && (
                              <span 
                                style={{ 
                                  fontFamily: 'var(--font-body)',
                                  fontSize: 'var(--text-sm)',
                                  color: 'var(--oxblood)',
                                  fontWeight: 400
                                }}
                              >
                                <Wine size={14} className="inline mr-1" />
                                {item.wines}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What's Included */}
                <div className="grid md:grid-cols-2 gap-8 mb-16">
                  <div className="p-8" style={{ backgroundColor: 'var(--linen)' }}>
                    <h3 
                      className="mb-6"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--charcoal)',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                      }}
                    >
                      What&apos;s Included
                    </h3>
                    <ul className="space-y-3">
                      {tour.included.map((item, index) => (
                        <li 
                          key={index}
                          className="flex items-start gap-3"
                        >
                          <div 
                            className="mt-1"
                            style={{ 
                              width: '6px',
                              height: '6px',
                              backgroundColor: 'var(--oxblood)',
                              borderRadius: '50%',
                              flexShrink: 0
                            }}
                          />
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              lineHeight: 'var(--line-height-body)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-8" style={{ backgroundColor: 'var(--linen)' }}>
                    <h3 
                      className="mb-6"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--charcoal)',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                      }}
                    >
                      What to Bring
                    </h3>
                    <ul className="space-y-3">
                      {tour.whatToBring.map((item, index) => (
                        <li 
                          key={index}
                          className="flex items-start gap-3"
                        >
                          <div 
                            className="mt-1"
                            style={{ 
                              width: '6px',
                              height: '6px',
                              backgroundColor: 'var(--copper)',
                              borderRadius: '50%',
                              flexShrink: 0
                            }}
                          />
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              lineHeight: 'var(--line-height-body)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="space-y-6 p-8" style={{ backgroundColor: 'var(--linen)' }}>
                  <div>
                    <h3 
                      className="mb-2"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--charcoal)',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Cancellation Policy
                    </h3>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        lineHeight: 'var(--line-height-body)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {tour.cancellationPolicy}
                    </p>
                  </div>

                  <div>
                    <h3 
                      className="mb-2"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--charcoal)',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Accessibility
                    </h3>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        lineHeight: 'var(--line-height-body)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {tour.accessibility}
                    </p>
                  </div>

                  <div>
                    <h3 
                      className="mb-2"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--charcoal)',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Languages
                    </h3>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        lineHeight: 'var(--line-height-body)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {tour.languages}
                    </p>
                  </div>
                </div>
              </div>

              {/* Booking Sidebar */}
              <div className="lg:col-span-2">
                <div className="sticky top-8">
                  <div className="p-8" style={{ backgroundColor: 'var(--linen)' }}>
                    <div className="mb-8">
                      <div 
                        className="mb-2"
                        style={{ 
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--text-4xl)',
                          fontWeight: 300,
                          color: 'var(--charcoal)'
                        }}
                      >
                        {tour.price}
                      </div>
                      <p 
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        All inclusive
                      </p>
                    </div>

                    <div className="mb-8">
                      <h3 
                        className="mb-4"
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--charcoal)',
                          fontWeight: 500,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Select Date
                      </h3>
                      <div className="space-y-3">
                        {tour.dates.map((dateOption, index) => (
                          <button
                            key={index}
                            className="w-full p-4 text-left border transition-all duration-200 hover:border-charcoal"
                            style={{ 
                              borderColor: 'var(--stone)',
                              backgroundColor: 'var(--paper)'
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div 
                                  className="mb-1"
                                  style={{ 
                                    fontFamily: 'var(--font-body)',
                                    fontSize: 'var(--text-base)',
                                    color: 'var(--charcoal)',
                                    fontWeight: 400
                                  }}
                                >
                                  {dateOption.date}
                                </div>
                                <div 
                                  style={{ 
                                    fontFamily: 'var(--font-body)',
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--slate)',
                                    fontWeight: 300
                                  }}
                                >
                                  {dateOption.spots} spots left
                                </div>
                              </div>
                              <span 
                                className="px-2 py-1"
                                style={{ 
                                  backgroundColor: dateOption.availability === 'Limited' ? 'var(--oxblood)' : 'var(--olive-mist)',
                                  color: dateOption.availability === 'Limited' ? 'var(--paper)' : 'var(--charcoal)',
                                  fontFamily: 'var(--font-body)',
                                  fontSize: 'var(--text-xs)',
                                  fontWeight: 400
                                }}
                              >
                                {dateOption.availability}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <button 
                      className="w-full px-6 py-4 mb-4 transition-opacity duration-200 hover:opacity-80"
                      style={{ 
                        backgroundColor: 'var(--charcoal)',
                        color: 'var(--paper)',
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-base)',
                        fontWeight: 400
                      }}
                    >
                      Book Now
                    </button>

                    <button 
                      className="w-full px-6 py-4 transition-colors duration-200 hover:bg-[var(--paper)]"
                      style={{ 
                        border: '1px solid var(--charcoal)',
                        backgroundColor: 'transparent',
                        color: 'var(--charcoal)',
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-base)',
                        fontWeight: 400
                      }}
                    >
                      Contact for Private Tour
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
